'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { usStates } from '@/data/us-states'

// ============================================================================
// Types & Schema
// ============================================================================

const taxcloudFormSchema = z.object({
	apiKey: z.string().min(1, 'API Key is required'),
	connectionId: z.string().uuid('Connection ID must be a valid UUID'),
	originLine1: z.string().min(1, 'Ship-from street is required'),
	originCity: z.string().min(1, 'Ship-from city is required'),
	originState: z
		.string()
		.refine(
			(value) => usStates.some((state) => state.code === value),
			'Ship-from state must be a 2-letter US state code',
		),
	originZip: z.string().min(1, 'Ship-from ZIP is required'),
	enableReporting: z.boolean(),
})

type FormValues = z.infer<typeof taxcloudFormSchema>

interface TaxcloudFormProps {
	defaultValues?: Record<string, any>
	onSubmit: (data: Record<string, any>) => Promise<void>
	isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function TaxcloudForm({
	defaultValues = {},
	onSubmit,
	isLoading = false,
}: TaxcloudFormProps) {
	const { t } = useTranslation('integrations')

	const form = useForm<FormValues>({
		resolver: zodResolver(taxcloudFormSchema),
		defaultValues: {
			apiKey: defaultValues.credentials?.apiKey ?? '',
			connectionId: defaultValues.credentials?.connectionId ?? '',
			originLine1: defaultValues.credentials?.originLine1 ?? '',
			originCity: defaultValues.credentials?.originCity ?? '',
			originState: defaultValues.credentials?.originState ?? '',
			originZip: defaultValues.credentials?.originZip ?? '',
			enableReporting: defaultValues.credentials?.enableReporting ?? false,
		},
	})

	const { register, handleSubmit, formState: { errors }, watch, setValue } = form

	const enableReporting = watch('enableReporting')
	const originState = watch('originState')

	const handleFormSubmit = async (data: FormValues) => {
		const credentials = data
		await onSubmit({ credentials })
	}

	return (
		<form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>{t('taxcloud.credentialsTitle')}</CardTitle>
					<CardDescription>{t('taxcloud.credentialsDescription')}</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="apiKey">{t('taxcloud.apiKeyLabel')}</Label>
						<Input
							id="apiKey"
							type="password"
							placeholder={t('taxcloud.apiKeyPlaceholder')}
							disabled={isLoading}
							{...register('apiKey')}
						/>
						{errors.apiKey && (
							<p className="text-xs text-destructive">{t('taxcloud.apiKeyRequired')}</p>
						)}
					</div>

					<div className="space-y-2">
						<Label htmlFor="connectionId">{t('taxcloud.connectionIdLabel')}</Label>
						<Input
							id="connectionId"
							placeholder={t('taxcloud.connectionIdPlaceholder')}
							disabled={isLoading}
							{...register('connectionId')}
						/>
						<p className="text-xs text-muted-foreground">{t('taxcloud.connectionIdHint')}</p>
						{errors.connectionId && (
							<p className="text-xs text-destructive">{t('taxcloud.connectionIdRequired')}</p>
						)}
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>{t('taxcloud.originTitle')}</CardTitle>
					<CardDescription>{t('taxcloud.originDescription')}</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="originLine1">{t('taxcloud.originLine1Label')}</Label>
						<Input
							id="originLine1"
							placeholder={t('taxcloud.originLine1Placeholder')}
							disabled={isLoading}
							{...register('originLine1')}
						/>
						{errors.originLine1 && (
							<p className="text-xs text-destructive">{t('taxcloud.originLine1Required')}</p>
						)}
					</div>

					<div className="space-y-2">
						<Label htmlFor="originCity">{t('taxcloud.originCityLabel')}</Label>
						<Input
							id="originCity"
							placeholder={t('taxcloud.originCityPlaceholder')}
							disabled={isLoading}
							{...register('originCity')}
						/>
						{errors.originCity && (
							<p className="text-xs text-destructive">{t('taxcloud.originCityRequired')}</p>
						)}
					</div>

					<div className="space-y-2">
						<Label htmlFor="originState">{t('taxcloud.originStateLabel')}</Label>
						<Select
							value={originState}
							onValueChange={(value) => setValue('originState', value, { shouldValidate: true })}
							disabled={isLoading}
						>
							<SelectTrigger id="originState" className="w-full">
								<SelectValue placeholder={t('taxcloud.originStatePlaceholder')} />
							</SelectTrigger>
							<SelectContent>
								{usStates.map((state) => (
									<SelectItem key={state.code} value={state.code}>
										{state.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="text-xs text-muted-foreground">{t('taxcloud.originStateHint')}</p>
						{errors.originState && (
							<p className="text-xs text-destructive">{t('taxcloud.originStateRequired')}</p>
						)}
					</div>

					<div className="space-y-2">
						<Label htmlFor="originZip">{t('taxcloud.originZipLabel')}</Label>
						<Input
							id="originZip"
							placeholder={t('taxcloud.originZipPlaceholder')}
							disabled={isLoading}
							{...register('originZip')}
						/>
						{errors.originZip && (
							<p className="text-xs text-destructive">{t('taxcloud.originZipRequired')}</p>
						)}
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>{t('taxcloud.reportingTitle')}</CardTitle>
					<CardDescription>{t('taxcloud.reportingDescription')}</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="flex items-center justify-between rounded-lg border p-4">
						<div className="space-y-0.5">
							<Label htmlFor="enableReporting">{t('taxcloud.enableReportingLabel')}</Label>
							<p className="text-xs text-muted-foreground">{t('taxcloud.enableReportingHint')}</p>
						</div>
						<Switch
							id="enableReporting"
							checked={enableReporting}
							onCheckedChange={(checked) => setValue('enableReporting', checked)}
							disabled={isLoading}
						/>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>{t('taxcloud.setupInstructions')}</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="rounded-lg bg-muted p-4">
						<ol className="list-inside list-decimal space-y-3 text-sm text-muted-foreground">
							<li>
								{t('taxcloud.setupStep1')}{' '}
								<a
									href="https://app.taxcloud.com/"
									target="_blank"
									rel="noopener noreferrer"
									className="text-primary underline"
								>
									{t('taxcloud.setupStep1Link')}
								</a>.
							</li>
							<li dangerouslySetInnerHTML={{ __html: t('taxcloud.setupStep2') }} />
							<li>{t('taxcloud.setupStep3')}</li>
							<li>{t('taxcloud.setupStep4')}</li>
						</ol>
					</div>
				</CardContent>
			</Card>
		</form>
	)
}
